import { Debt, DebtDetails, Installment } from '../types';

const EPS = 0.005;

function isInstallmentPaid(i: Installment): boolean {
  const status = typeof i.status === 'string' ? i.status.trim().toUpperCase() : '';
  return status === 'PAID' || i.isPaid === true || i.paid === true;
}

function remainingFromDebt(debt: Debt): number | undefined {
  const r = debt.remainingBalance ?? debt.remainingAmount;
  if (r !== undefined && r !== null && Number.isFinite(r)) {
    return r;
  }
  const totalPaid = debt.totalPaid;
  const totalDebt = debt.totalDebt;
  if (
    totalPaid !== undefined &&
    totalDebt !== undefined &&
    Number.isFinite(totalPaid) &&
    Number.isFinite(totalDebt)
  ) {
    return Math.max(0, totalDebt - totalPaid);
  }
  return undefined;
}

/**
 * True only when we are confident there is nothing left to collect.
 * Prefer installment rows when present; avoid hiding actions on incomplete or inconsistent API data.
 */
export function isDebtFullyPaid(
  debt: Debt,
  debtDetails: DebtDetails | null | undefined
): boolean {
  if (!debtDetails) {
    return false;
  }

  const expected = debtDetails.numberOfInstallments ?? 0;
  const list = debtDetails.installments ?? [];

  // Authoritative: full schedule returned — all rows must be paid
  if (list.length > 0) {
    return list.every(isInstallmentPaid);
  }

  // No rows but API says there should be installments: only trust explicit remaining / implied remaining
  if (expected > 0) {
    const remaining = remainingFromDebt(debt);
    if (remaining !== undefined) {
      return remaining <= EPS;
    }
    // Cannot infer safely — keep actions visible
    return false;
  }

  // No planned installments (expected === 0) and no rows: fall back to balances on debt summary
  const remaining = remainingFromDebt(debt);
  if (remaining !== undefined) {
    return remaining <= EPS;
  }

  return false;
}
